import React, { useMemo } from 'react';
import { Entity, Example, LabelDefinition } from '../types';
import EntitySpans from './EntitySpans';
import { HUMAN_MODEL } from '../utils/entityUtils';

interface DiffViewProps {
  example: Example;
  modelNames: string[];
  /** Optional pair selector. When BOTH are set, only those two model rows
   *  (plus Gold) are rendered. If unset (or one of them unset), all model
   *  rows are rendered alongside Gold. */
  selectedModelA?: string;
  selectedModelB?: string;
  customLabelColors?: Record<string, string>;
  /** BQ-15: promote a model prediction onto humanAnnotations. */
  onPromoteEntity?: (exampleId: string, modelName: string, entity: Entity) => void;
  /** BQ-15: reject a model prediction (excluded from FP / errors-vs-gold). */
  onRejectEntity?: (exampleId: string, modelName: string, entity: Entity) => void;
  /** BQ-18: per-label annotation guidelines, threaded down to EntitySpans. */
  labelDefinitions?: Record<string, LabelDefinition>;
}

/**
 * BQ-16: Aligned three-row (or N+1-row) diff view. Renders the same
 * `example.text` across a Gold row plus one row per included model, each as
 * a read-only `<EntitySpans>`. Visual identity is carried by a small label
 * tag on the left of each row; the text body is identical across rows so
 * the eye can scan vertically for divergence.
 *
 * Sort: Gold first; remaining models in alphabetical order (deterministic,
 * decoupled from the `modelNames` array order in the source data).
 *
 * Out of scope here (see BQ acceptance criteria):
 *  - Disagreement highlighting on the spans themselves (BQ-18 territory).
 *  - Promote/reject buttons (BQ-15 will compose on top of this).
 */
const DiffView: React.FC<DiffViewProps> = ({
  example,
  modelNames,
  selectedModelA,
  selectedModelB,
  customLabelColors,
  onPromoteEntity,
  onRejectEntity,
  labelDefinitions,
}) => {
  const visibleModels = useMemo(() => {
    const pair = selectedModelA && selectedModelB ? [selectedModelA, selectedModelB] : null;
    const candidates = pair
      ? modelNames.filter((m) => pair.includes(m))
      : [...modelNames];
    // Deterministic alphabetical sort regardless of source order.
    return candidates.sort((a, b) => a.localeCompare(b));
  }, [modelNames, selectedModelA, selectedModelB]);

  const goldEntities = example.humanAnnotations || [];

  // BQ-15: predicates for promoted / rejected visual state on the per-model
  // rows. Computed inline here since DiffView is presentational.
  const isPromoted = (entity: Entity) =>
    (example.humanAnnotations ?? []).some(
      (e) => e.start === entity.start && e.end === entity.end && e.label === entity.label
    );
  const isRejectedFor = (modelName: string) => (entity: Entity) =>
    (example.rejectedPredictions ?? []).some(
      (r) =>
        r.modelName === modelName &&
        r.entity.start === entity.start &&
        r.entity.end === entity.end &&
        r.entity.label === entity.label
    );

  return (
    <div className="space-y-2">
      {/* Gold row, always first. Read-only here — gold editing remains on the
          card-grid view (ComparisonView header) so the diff view stays a
          pure presentational comparator. */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        <span
          className="shrink-0 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-0.5 mt-0.5"
          title="Human-annotated reference"
        >
          {HUMAN_MODEL}
        </span>
        <div className="flex-1 text-sm text-gray-900">
          <EntitySpans
            text={example.text}
            entities={goldEntities}
            readOnly
            customColors={customLabelColors}
            labelDefinitions={labelDefinitions}
          />
        </div>
      </div>

      {/* Model rows. */}
      {visibleModels.map((modelName) => {
        const response = example.modelResponses.find((r) => r.modelName === modelName);
        const entities = response?.entities ?? [];
        return (
          <div
            key={`diff-row-${modelName}`}
            className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded px-3 py-2"
          >
            <span
              className="shrink-0 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded px-2 py-0.5 mt-0.5"
              title={`Predictions from ${modelName}`}
            >
              {modelName}
            </span>
            <div className="flex-1 text-sm text-gray-800">
              {response ? (
                <EntitySpans
                  text={example.text}
                  entities={entities}
                  readOnly
                  customColors={customLabelColors}
                  labelDefinitions={labelDefinitions}
                  onPromote={
                    onPromoteEntity
                      ? (entity) => onPromoteEntity(example.id, modelName, entity)
                      : undefined
                  }
                  onReject={
                    onRejectEntity
                      ? (entity) => onRejectEntity(example.id, modelName, entity)
                      : undefined
                  }
                  isPromoted={isPromoted}
                  isRejected={isRejectedFor(modelName)}
                />
              ) : (
                <span className="text-gray-400 italic">No response from {modelName}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DiffView;
